/* ===== PHASE 6: AMBIENT ORBS, PARALLAX, SHIMMER, SPLIT REVEAL, STAGGER ===== */

(function () {
  const noMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (noMotion) return;

  /* Idea 46 — Ambient floating orbs */
  function initAmbientOrbs() {
    const canvas = document.createElement('div');
    canvas.className = 'idx-ambient-canvas';
    canvas.setAttribute('aria-hidden', 'true');

    for (let i = 1; i <= 3; i++) {
      const orb = document.createElement('div');
      orb.className = `idx-orb idx-orb--${i}`;
      canvas.appendChild(orb);
    }

    if (document.body) {
      document.body.insertBefore(canvas, document.body.firstChild);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.insertBefore(canvas, document.body.firstChild);
      });
    }
  }

  /* Idea 48 — Add gradient shimmer class to h2 headings */
  function initGradientHeadings() {
    const sectionIds = [
      'shopify-section-lookbook_cards_VFTcQq',
      'shopify-section-collections_showcase_cxUrL8',
      'shopify-section-newsletter_rFpTxK',
      'shopify-section-video_gallery_preview_hPvR3x',
    ];

    sectionIds.forEach((id) => {
      const section = document.getElementById(id);
      if (section) {
        const h2 = section.querySelector('h2');
        if (h2) {
          h2.classList.add('idx-h2-shimmer');
        }
      }
    });
  }

  /* Idea 47 — Hero parallax scroll */
  let parallaxAttached = false;
  let parallaxImages = null;
  let unsubscribeParallax = null;

  // The parallax is desktop-only, but the viewport test used to live inside the
  // per-frame callback, so a phone still woke a frame on every scroll event to
  // read window.innerWidth and return having done nothing. Reading the viewport
  // while layout is dirty forces a synchronous recalculation, and that dead path
  // measured 489ms of forced layout on a 412px viewport under a 6x CPU throttle
  // - pure waste on exactly the devices that cannot afford it. The decision is
  // now made once, before the coordinator subscription is created.
  // The gate is a media query rather than a window.innerWidth comparison. The
  // browser maintains the match state itself, so reading `.matches` costs no
  // layout, and `change` fires only when the breakpoint is actually crossed.
  // Re-checking the viewport from a resize handler instead still measured
  // 262ms of forced layout on a 412px viewport, because the document resizes
  // constantly while images and lazy sections land and each of those callbacks
  // read the viewport while layout was dirty. On a phone this listener now
  // never fires at all.
  const PARALLAX_MIN_WIDTH = 768;
  const parallaxQuery = window.matchMedia(`(min-width: ${PARALLAX_MIN_WIDTH}px)`);

  // This used to run getElementById/querySelectorAll inside the per-frame
  // callback, on its own requestAnimationFrame. That is exactly the pattern
  // the shared scroll coordinator (assets/scroll-coordinator.js) exists to
  // remove: an independent rAF landing in the same frame as header.js,
  // engagement-signals, and everything else that reads/writes scroll-driven
  // style. The node list is cached once when the breakpoint gate attaches,
  // and re-cached only when the gate crosses again (matchMedia `change`),
  // so the read phase touches no DOM beyond the shared metrics.
  function cacheParallaxImages() {
    const slideshow = document.getElementById('shopify-section-slideshow_q3U4Jn');
    parallaxImages = slideshow
      ? slideshow.querySelectorAll('.f-slideshow__slide-image, .media img')
      : null;
  }

  function readParallax(m) {
    return m.scrollY;
  }

  function writeParallax(scrollY) {
    if (!parallaxImages) {
      return;
    }
    parallaxImages.forEach((img) => {
      img.style.transform = `translateY(${scrollY * 0.32}px)`;
    });
  }

  function clearParallax() {
    const slideshow = document.getElementById('shopify-section-slideshow_q3U4Jn');
    if (!slideshow) {
      return;
    }
    slideshow.querySelectorAll('.f-slideshow__slide-image, .media img').forEach((img) => {
      img.style.transform = '';
    });
  }

  // Attaching and detaching on a breakpoint crossing keeps a desktop visitor
  // who narrows the window from paying for a handler that would do nothing,
  // and clears any offset the images were left holding when it stops applying.
  function syncParallaxListener() {
    const applies = parallaxQuery.matches;
    if (applies === parallaxAttached) {
      return;
    }
    parallaxAttached = applies;

    if (applies) {
      cacheParallaxImages();
      unsubscribeParallax = window.CoraScroll.subscribe({
        name: 'index-hero-parallax',
        read: readParallax,
        write: writeParallax,
      });
      return;
    }

    if (unsubscribeParallax) {
      unsubscribeParallax();
      unsubscribeParallax = null;
    }
    parallaxImages = null;
    clearParallax();
  }

  /* Idea 53 — Image-with-text split reveal */
  function initSplitReveal() {
    const section = document.getElementById('shopify-section-image_with_text_jA6WUk');
    if (!section) {
      return;
    }

    const mediaEl = section.querySelector('.image-with-text__media');
    const innerEl = section.querySelector('.image-with-text__inner');

    if (mediaEl) {
      mediaEl.classList.add('idx-split-image');
    }
    if (innerEl) {
      innerEl.classList.add('idx-split-text');
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (mediaEl) {
              mediaEl.classList.add('idx-split-active');
            }
            if (innerEl) {
              innerEl.classList.add('idx-split-active');
            }
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    observer.observe(section);
  }

  /* Idea 54 — Video gallery stagger pop-in */
  function initVideoGalleryStagger() {
    const section = document.getElementById('shopify-section-video_gallery_preview_hPvR3x');
    if (!section) {
      return;
    }

    // Try multiple card selectors
    const selectors = ['.card', '[class*="vgp-card"]', '[class*="video-card"]', '.f-card'];

    let cards = [];
    for (const sel of selectors) {
      const found = section.querySelectorAll(sel);
      if (found.length > 0) {
        cards = Array.from(found);
        break;
      }
    }

    if (cards.length === 0) {
      return;
    }

    cards.forEach((card, index) => {
      card.classList.add('idx-vg-card');
      card.style.setProperty('--idx-vg-i', index);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('idx-vg-in');
          }
        });
      },
      { threshold: 0.08 }
    );

    observer.observe(section);
  }

  /* Initialize on DOMContentLoaded */
  function init() {
    initAmbientOrbs();
    initGradientHeadings();
    initSplitReveal();
    initVideoGalleryStagger();
    syncParallaxListener();

    // addListener is the Safari 13 and earlier spelling; MediaQueryList only
    // became an EventTarget in Safari 14.
    if (typeof parallaxQuery.addEventListener === 'function') {
      parallaxQuery.addEventListener('change', syncParallaxListener);
    } else if (typeof parallaxQuery.addListener === 'function') {
      parallaxQuery.addListener(syncParallaxListener);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Cleanup on beforeunload */
  window.addEventListener('beforeunload', () => {
    if (unsubscribeParallax) {
      unsubscribeParallax();
      unsubscribeParallax = null;
    }
  });
})();
